import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useChartLocale } from 'components/charts/hooks/use-chart-locale';
import { BenchmarkTimeGrouping } from 'views/benchmark-analytics/types/benchmark-filters.type';

const EMPTY_VALUE = '–';

// Numbers follow the chart number locale, so cards, table and tooltips match the axes.
// Day and month labels follow the app language. Week and quarter keep their key form ('2026-W01', '2026-Q1').
export const useBenchmarkFormatters = () => {
  const { locale } = useChartLocale();
  const intl = useIntl();

  return useMemo(() => {
    const fixedFormatters = new Map<string, Intl.NumberFormat>();
    const fixed = (decimals: number, signed: boolean): Intl.NumberFormat => {
      const cacheKey = `${decimals}:${signed}`;
      const cached = fixedFormatters.get(cacheKey);
      if (cached) return cached;

      const formatter = new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        signDisplay: signed ? 'exceptZero' : 'auto',
      });
      fixedFormatters.set(cacheKey, formatter);
      return formatter;
    };
    const countFormatter = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
    const compactDayFormatter = new Intl.DateTimeFormat(intl.locale, { day: 'numeric', month: 'short' });
    const dayFormatter = new Intl.DateTimeFormat(intl.locale, { day: 'numeric', month: 'short', year: 'numeric' });
    const compactMonthFormatter = new Intl.DateTimeFormat(intl.locale, { month: 'short' });
    const monthFormatter = new Intl.DateTimeFormat(intl.locale, { month: 'short', year: 'numeric' });
    const toLocalDate = (date: string): Date => {
      const [year, month, day] = date.split('-').map(Number);
      return new Date(year, month - 1, day);
    };
    const formatPeriodLabel = (key: string, grouping: BenchmarkTimeGrouping): string => {
      switch (grouping) {
        case 'day':
          return dayFormatter.format(toLocalDate(key));
        case 'month':
          return monthFormatter.format(toLocalDate(`${key}-01`));
        case 'week':
        case 'quarter':
          return key;
      }
    };
    // Shorter tick labels. The chart's year markers carry the year.
    const formatAxisPeriodLabel = (key: string, grouping: BenchmarkTimeGrouping): string => {
      switch (grouping) {
        case 'day':
          return compactDayFormatter.format(toLocalDate(key));
        case 'week': {
          const [, week] = key.split('-W');
          return `W${week}`;
        }
        case 'month':
          return compactMonthFormatter.format(toLocalDate(`${key}-01`));
        case 'quarter':
          return key.slice(5);
      }
    };

    return {
      formatValue: (value: number | null, decimals: number): string =>
        value === null ? EMPTY_VALUE : fixed(decimals, false).format(value),
      formatDelta: (value: number | null, decimals: number): string =>
        value === null ? EMPTY_VALUE : fixed(decimals, true).format(value),
      formatCount: (value: number): string => countFormatter.format(value),
      formatPeriodLabel,
      formatAxisPeriodLabel,
    };
  }, [intl.locale, locale]);
};
